import { MouseEvent, RefObject, useRef } from 'react';
import { Point } from 'common/types';

// #region useFacetSelection
type UseFacetSelectionReturnValue = {
  onMouseMove: (ev: MouseEvent) => void;
  handleFacetHover: (event: MouseEvent) => void;
  handleFacetListMouseLeave: () => void;
  isControlledSelectionMode: boolean;
};

const TRACKING_MOUSE_LOCS = 3;   // number of recent mouse locations to track
const TRACKING_DELAY      = 250; // how long to wait before publishing the hover event

/**
 * Functionality that tracks facet selection when user hovers over the facet items (when the side overlay is active)
 */
function useFacetSelection(selectedFacet: string, onFacetSelect: (facetName: string) => void, listRef: RefObject<HTMLDivElement>): UseFacetSelectionReturnValue {
  const asyncFacetHandlerRef = useRef<number | undefined>(undefined);
  const isControlledSelectionMode = !!selectedFacet;

  /**
   * logic that tracks mouse movement direction as a list of N latest locations
   */
  const { current: mouseLocations } = useRef<Point[]>([]);
  const onMouseMove = (ev: MouseEvent): void => {
    // Capture mouse movements in a fixed size queue
    mouseLocations.push({ x: ev.pageX, y: ev.pageY });
    if (mouseLocations.length > TRACKING_MOUSE_LOCS) {
      mouseLocations.shift();
    }
  };

  const handleFacetHover = (e: MouseEvent): void => {
    const { facet } = (e.currentTarget as HTMLElement).dataset;

    // Verify that we're in the controlled mode and calculation is possible
    if (!facet || !isControlledSelectionMode) {
      return;
    }

    // Cleanup previously scheduled handler
    if (typeof asyncFacetHandlerRef.current === 'number') {
      window.clearTimeout(asyncFacetHandlerRef.current);
    }

    /**
     * An IIFE that calculates if mouse is moving towards facet overlay,
     * so that the user does not have to move the cursor exactly sideways to keep seeing the same facet in the details popup.
     *
     * It works by checking for movement within a triangle that goes from the last mouse location towards the right side of the list.
     * The logic was adapted from a jQuery plugin called "menu-aim": {@link https://github.com/kamens/jQuery-menu-aim}
     */
    const isMovingTowardsOverlay = ((): boolean => {
      const listEl = listRef.current;

      // Verify that calculation is possible during this render
      if (!isControlledSelectionMode) { return false; }
      if (!listEl) { return false; }

      const upperLeft: Point = { x: listEl.offsetLeft, y: listEl.offsetTop };
      const upperRight: Point = { x: listEl.offsetLeft + listEl.offsetWidth, y: upperLeft.y };
      const lowerLeft: Point = { x: listEl.offsetLeft, y: listEl.offsetTop + listEl.offsetHeight };
      const lowerRight: Point = { x: listEl.offsetLeft + listEl.clientWidth, y: lowerLeft.y };
      const loc = mouseLocations[mouseLocations.length - 1];
      let prevLoc = mouseLocations[0];
      if (!loc) {
        return false;
      }
      if (!prevLoc) {
        prevLoc = loc;
      }

      // Cursor is not even over the list
      if (prevLoc.x < listEl.offsetLeft || prevLoc.x > lowerRight.x || prevLoc.y < listEl.offsetTop || prevLoc.y > lowerRight.y) {
        return false;
      }

      const slope = (a: Point, b: Point): number => (b.y - a.y) / (b.x - a.x);

      // Determine whether user is moving mouse towards the right side of the list
      const decreasingSlope = slope(loc, upperRight);
      const increasingSlope = slope(loc, lowerRight);
      const prevDecreasingSlope = slope(prevLoc, upperRight);
      const prevIncreasingSlope = slope(prevLoc, lowerRight);
      if (decreasingSlope < prevDecreasingSlope && increasingSlope > prevIncreasingSlope) {
        return true;
      }

      return false;
    })();

    // Update newly hovered facet to be the 'selected' facet
    if (isMovingTowardsOverlay) {
      asyncFacetHandlerRef.current = window.setTimeout(() => {
        onFacetSelect(facet);
        asyncFacetHandlerRef.current = undefined;
      }, TRACKING_DELAY);
    } else {
      onFacetSelect(facet);
    }
  };

  // Cleanup all in-flight work if user moves mouse outside of the facet list container
  const handleFacetListMouseLeave = (): void => {
    if (!selectedFacet) { return; }

    window.clearTimeout(asyncFacetHandlerRef.current);
  };

  return {
    onMouseMove, handleFacetHover, handleFacetListMouseLeave, isControlledSelectionMode,
  };
}
// #endregion

export { useFacetSelection };
